<template>
  <FullCalendar
    id="calendar"
    v-if="appointmentCapacities"
    :options="calendarOptions"
    ref="fullCalendar"
  >
    <template #eventContent="arg">
      <div class="event-content">
        <div class="event-first-line">
          <b-badge
            class="event-badge-type"
            :variant="getAppointmentTypeColor(arg.event.extendedProps.appointmentType)"
          >
            {{ $t(arg.event.extendedProps.appointmentType) }}
          </b-badge>
          <template v-if="!arg.event.extendedProps.appointment">
            <b-badge class="event-badge-free">
              {{ $t('appointmentCapacityFree') }}
            </b-badge>
            <b-badge v-if="arg.event.extendedProps.employer" class="event-badge-employer">
              {{ arg.event.extendedProps.employer.name }}
            </b-badge>
          </template>
          <b-badge
            v-if="arg.event.extendedProps.appointment"
            class="event-badge-state"
            :variant="
              getAppointmentStateColor(arg.event.extendedProps.appointment.appointmentState)
            "
          >
            {{ $t(arg.event.extendedProps.appointment.appointmentState) }}
          </b-badge>
          <b-badge
            v-if="arg.event.extendedProps.appointment"
            class="event-badge-approved"
            :variant="
              getYesNoStateColor(arg.event.extendedProps.appointment.appointmentApprovedState)
            "
          >
            {{ appointmentApprovedStateSpelledOut(arg.event.extendedProps.appointment) }}
          </b-badge>
        </div>
      </div>
      <div class="event-employees event-icon-row pl-1" :title="$t('employees')">
        <i class="fa fa-fw fa-user event-icon-row-icon" aria-hidden="true"></i>
        <span class="event-icon-row-text">{{ employeeDisplayText(arg.event.extendedProps) }}</span>
      </div>
      <template v-if="arg.event.extendedProps.appointment">
        <div class="event-icon-row pl-1">
          <i class="fa fa-fw fa-clock-o event-icon-row-icon" aria-hidden="true"></i>
          <span class="event-icon-row-text">
            {{ $time(arg.event.extendedProps.appointment.installationStartAt) }} -
            {{ $time(arg.event.extendedProps.appointment.installationEndAt) }}
          </span>
        </div>
        <div class="event-row event-icon-row pl-1 pb-1">
          <template v-if="arg.event.extendedProps.appointment.project">
            <i class="fa fa-fw fa-map-marker event-icon-row-icon" aria-hidden="true"></i>
            <span class="event-icon-row-text">
              {{ appointmentProjectSummary(arg.event.extendedProps.appointment.project) }}
            </span>
          </template>
        </div>
      </template>
    </template>
  </FullCalendar>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import de from '@fullcalendar/core/locales/de';
import {
  getAppointmentTypeColor,
  getAppointmentTypeHexColor,
  getAppointmentStateColor,
  getYesNoStateColor,
} from '@/helpers/colors';
import { getProjectInstallationZip } from '@/helpers/projects';

const EMPLOYEE_PLACEHOLDER = '–';

export default {
  components: {
    FullCalendar,
  },
  props: {
    selectEventCallback: Function,
    dateChangedCallback: Function,
  },
  data() {
    return {
      startDate: null,
      endDate: null,

      calendarOptions: {
        displayEventTime: false,
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
        },
        locale: de,
        plugins: [timeGridPlugin, dayGridPlugin, interactionPlugin],
        initialView: 'dayGridWeek',
        weekends: false,
        selectable: true,
        events: this.appointmentCapacities,
        eventClick: this.handleEventClick,
        eventDidMount: this.handleEventDidMount,
        eventOrder: ['stateOrder', 'appointmentTypeOrder', 'prio'],
        customButtons: {
          customPrev: {
            text: 'Prev',
            click: this.handlePrevClick,
          },
          customNext: {
            text: 'Next',
            click: this.handleNextClick,
          },
          customToday: {
            text: 'Heute',
            click: this.handleTodayClick,
          },
          customShowWeekend: {
            text: this.$t('showWeekend'),
            click: () => this.setWeekendVisible(true),
          },
          customHideWeekend: {
            text: this.$t('hideWeekend'),
            click: () => this.setWeekendVisible(false),
          },
          customDayGridWeek: {
            text: 'Woche',
            click: this.handleDayGridWeekClick,
          },
          customDayGridMonth: {
            text: 'Monat',
            click: this.handleDayGridMonthClick,
          },
        },
        headerToolbar: {
          left: 'customPrev,customNext,customToday',
          center: 'title',
          right: 'customShowWeekend,customHideWeekend customDayGridWeek,customDayGridMonth',
        },
        buttonIcons: {
          customNext: 'chevron-right',
          customPrev: 'chevron-left',
        },
        weekNumbers: true,
      },
    };
  },
  computed: {
    ...mapGetters(['getAppointmentCapacities']),
    appointmentCapacities() {
      return this.getAppointmentCapacities;
    },
  },
  methods: {
    getAppointmentTypeColor,
    getAppointmentStateColor,
    getYesNoStateColor,
    appointmentProjectSummary(project) {
      if (!project?.customer) {
        return '';
      }

      const parts = [
        '#' + project.number.toString(),
        project.customer.lastname,
        project.customer.firstname,
      ];
      const zip = getProjectInstallationZip(project);
      if (zip) {
        parts.push(zip);
      }

      return parts.join(', ');
    },
    hasEmployees(appointmentCapacity) {
      if (appointmentCapacity.appointment && appointmentCapacity.appointment.employees.length > 0) {
        return true;
      }

      return appointmentCapacity.employees.length > 0;
    },

    employeeDisplayText(appointmentCapacity) {
      if (this.hasEmployees(appointmentCapacity)) {
        return this.firstnameShortFullLastname(appointmentCapacity);
      }

      return EMPLOYEE_PLACEHOLDER;
    },

    /**
     * Generate a string with the initials of the employees that are assigned to a apointmentCapacity/appointment
     * If an appointment is present in the apointmentCapacity use the employees from the appointment, else use the employees from the apointmentCapacity
     */
    employeeShortcodes(appointmentCapacity) {
      const employeeList = appointmentCapacity.appointment
        ? appointmentCapacity.appointment.employees
        : appointmentCapacity.employees;
      let employeeString = '';
      employeeString = employeeList.reduce((acc, employee, index) => {
        const space = this.isFirstCharDigit(employee.firstname) ? ' ' : '';
        const initials =
          this.nameToInitial(employee.firstname) + space + this.nameToInitial(employee.lastname);

        if (index === 0) {
          return initials ? acc + initials : acc; // Skip if both first and last names are null or undefined
        } else {
          return initials ? acc + ', ' + initials : acc; // Skip if both first and last names are null or undefined
        }
      }, employeeString);

      return employeeString;
    },

    appointmentApprovedStateSpelledOut(appointment) {
      if (!appointment || !appointment.appointmentApprovedState) {
        return this.$t('appointmentApprovedState_no');
      }

      return appointment.appointmentApprovedState === 'YES'
        ? this.$t('appointmentApprovedState_yes')
        : this.$t('appointmentApprovedState_no');
    },

    firstnameShortFullLastname(appointmentCapacity) {
      const employeeList = appointmentCapacity.appointment
        ? appointmentCapacity.appointment.employees
        : appointmentCapacity.employees;
      let employeeString = '';
      employeeString = employeeList.reduce((acc, employee, index) => {
        const firstInitial = this.nameToInitial(employee.firstname);
        const space = this.isFirstCharDigit(employee.firstname) ? ' ' : '';
        const initials = firstInitial
          ? firstInitial + '.' + space + (employee.lastname || '')
          : employee.lastname || '';

        if (index === 0) {
          return initials ? acc + initials : acc; // Skip if both first and last names are null or undefined
        } else {
          return initials ? acc + ', ' + initials : acc; // Skip if both first and last names are null or undefined
        }
      }, employeeString);

      return employeeString;
    },

    /**
     * Format a name to a single character or if numeric return as is
     * @param {*} name
     */
    nameToInitial(name) {
      if (!name) return '';
      if (this.isFirstCharDigit(name)) return name;
      return name.charAt(0);
    },

    isFirstCharDigit(str) {
      return /^[0-9]/.test(str);
    },

    handleEventClick: function(data) {
      if (this.selectEventCallback) {
        this.selectEventCallback(data.event.id);
      }
    },

    handleEventDidMount({ event, el }) {
      this.applyBookedEventStyles(event, el);
    },

    applyBookedEventStyles(event, el) {
      if (!el) {
        return;
      }

      if (!event.extendedProps.appointment) {
        el.style.removeProperty('--appointment-type-color');
        return;
      }

      const typeColor = getAppointmentTypeHexColor(event.extendedProps.appointmentType);
      if (typeColor) {
        el.style.setProperty('--appointment-type-color', typeColor);
      } else {
        el.style.removeProperty('--appointment-type-color');
      }
    },

    syncCalendarEvents() {
      if (!this.$refs.fullCalendar) {
        return;
      }

      const calendarApi = this.$refs.fullCalendar.getApi();
      const events = this.appointmentCapacities;
      this.calendarOptions.events = events;
      calendarApi.removeAllEvents();
      events.forEach((eventInput) => {
        calendarApi.addEvent(eventInput);
      });
    },

    handlePrevClick() {
      this.$refs.fullCalendar.getApi().prev();
      this.updateDateInfo();
    },

    handleNextClick() {
      this.$refs.fullCalendar.getApi().next();
      this.updateDateInfo();
    },

    handleTodayClick() {
      this.$refs.fullCalendar.getApi().today();
      this.updateDateInfo();
    },

    handledayGridWeekClick() {
      this.$refs.fullCalendar.getApi().changeView('dayGridWeek');
      this.updateDateInfo();
    },

    handleDayGridWeekClick() {
      this.$refs.fullCalendar.getApi().changeView('dayGridWeek');
      this.updateDateInfo();
    },

    handleDayGridMonthClick() {
      this.$refs.fullCalendar.getApi().changeView('dayGridMonth');
      this.updateDateInfo();
    },

    setWeekendVisible(isVisible) {
      this.calendarOptions.weekends = isVisible;
      if (this.$refs.fullCalendar) {
        this.$refs.fullCalendar.getApi().setOption('weekends', isVisible);
      }
      this.updateDateInfo();
    },

    updateDateInfo() {
      this.getCurrentDateRange();
      this.dateChangedCallback({ startDate: this.startDate, endDate: this.endDate });
    },

    refreshCalendar() {
      this.syncCalendarEvents();
    },

    getCurrentDateRange() {
      if (this.$refs.fullCalendar) {
        const calendarApi = this.$refs.fullCalendar.getApi();
        const view = calendarApi.view;
        this.startDate = view.activeStart;
        this.endDate = view.activeEnd;
        // console.log('Current Date Range:', this.startDate, this.endDate);
        // Use startDate and endDate for further processing
      } else {
        console.warn('Calendar instance not yet available');
      }
    },
  },

  async mounted() {
    this.calendarOptions.events = this.appointmentCapacities;
  },

  watch: {
    appointmentCapacities(newValue) {
      this.calendarOptions.events = newValue;
    },
  },
};
</script>

<style scoped lang="scss">
$booked-tint-percent: 25%;
$free-slot-badge-color: #808080;
$detail-row-spacing: 1px;
$detail-first-row-spacing-top: 6px;

#calendar {
  width: 100%;
  margin: 0 auto;
}

/* Neutral badges of free slots, same hue as the free slot background. */
#calendar ::v-deep .badge.event-badge-free,
#calendar ::v-deep .badge.event-badge-employer {
  background-color: $free-slot-badge-color;
  color: white;
}

#calendar ::v-deep .fc-daygrid {
  .fc-daygrid-day-events {
    padding-left: 2px;
    padding-right: 10px;
  }

  /* FC positions harnesses with inline top/marginTop; gap via event box size (segHeights). */
  .fc-daygrid-event {
    margin-top: 6px;
    margin-bottom: 6px;
    border-radius: 3px;
  }
}

#calendar ::v-deep .fc-daygrid .fc-daygrid-event.is-booked {
  --fc-event-bg-color: color-mix(
    in srgb,
    var(--appointment-type-color) #{$booked-tint-percent},
    white
  );
  --fc-event-border-color: transparent;
  border: 0;
  border-left: 4px solid var(--appointment-type-color);
  background-color: color-mix(
    in srgb,
    var(--appointment-type-color) #{$booked-tint-percent},
    white
  );
}

::v-deep .pl-1 {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

::v-deep .event-row {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 1.2em;
}

::v-deep .badge {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: 0;
}

::v-deep .event-content {
  margin-bottom: 0;
}

::v-deep .event-first-line {
  display: flex;
  width: 100%;
  margin-bottom: 0;
  gap: 2px;
}

::v-deep .event-badge-type {
  width: 25%;
  min-width: 0;
}

/* Free slots use the same badge grid as booked slots: free = state, employer = approved. */
::v-deep .event-badge-state,
::v-deep .event-badge-free {
  width: 40%;
  min-width: 0;
}

::v-deep .event-badge-approved,
::v-deep .event-badge-employer {
  width: 35%;
  min-width: 0;
}

/* Row with a leading icon (employees, time, project). fa-fw keeps the texts aligned. */
::v-deep .event-icon-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding-top: $detail-row-spacing;
  padding-bottom: $detail-row-spacing;
}

::v-deep .event-icon-row-icon {
  flex-shrink: 0;
  color: #495057;
}

::v-deep .event-icon-row-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* First detail row: more space to the badge line above. */
::v-deep .event-employees {
  margin-top: 0;
  width: 100%;
  box-sizing: border-box;
  padding-top: $detail-first-row-spacing-top;
}
</style>
